import React, { useEffect, useState } from 'react';
import { MonitorHead, StatusBadge } from '../../components';
import { LastFetchNavIcon } from '../../components/navIcons';
import { apiService } from '../../services';
import type { LastFetchUser } from '../../types';
import { formatLastFetchStamp } from '../../utils/monitorFormat';
import { quietFor } from '../../utils/quietFor';
import '../monitoring/monitoring.css';

export const LastFetch: React.FC = () => {
  const [users, setUsers] = useState<LastFetchUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService
      .getLastFetch()
      .then((res) => {
        if (cancelled) return;
        if (res.success) setUsers(res.data.users);
        else setError(res.message || 'Failed to load last fetch');
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load last fetch');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="monitor-page">
      <main className="monitor-page__content">
        <MonitorHead
          title="Last fetch"
          icon={<LastFetchNavIcon size={20} />}
          subtitle="Who has gone quiet, stalest first. Only users with a prompt stored in the last 90 days are listed."
          aside={<span className="monitor-pill">Fixed window · 90 days</span>}
        />

        {error && <p className="monitor-error">{error}</p>}
        {loading && <p className="monitor-empty">Loading…</p>}
        {!loading && !error && users.length === 0 && (
          <p className="monitor-empty">No users with a stored prompt in the last 90 days.</p>
        )}

        {users.length > 0 && (
          <div className="monitor-card">
            <div className="monitor-table-wrap">
              <table className="monitor-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Last fetch</th>
                    <th>Quiet for</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((row) => {
                    const quiet = quietFor(row.last_fetch);
                    return (
                      <tr key={`${row.name}-${row.last_fetch}`}>
                        <td>{row.name}</td>
                        <td className="mono">{formatLastFetchStamp(row.last_fetch)}</td>
                        <td>
                          <StatusBadge tone={quiet.tone}>{quiet.label}</StatusBadge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <p className="monitor-note">
          Last fetch is the time of the latest prompt stored for that person, not the last time a prompt was scored.
          A user with no prompt in the last 90 days is left off. Rows do not open a prompt.
        </p>
      </main>
    </div>
  );
};

export default LastFetch;
